import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ZERO_QTY, qty, subQty, type LineSourceDef } from '@project/contracts'
import { Button, Icon, Input, Modal, Notice, Table, type ColumnDef } from '../design/index.js'
import { useApi, type LineRow, type PickLine, type PickResult } from '../app/api.js'
import { messageFor } from './feedback.js'
import { formatQuantity, formatRate } from './format.js'

/**
 * The controls behind `def.lines.sources`.
 *
 * The estimate picker is the one that matters: a line picked from the BOQ
 * carries `fromLineId`, so the ledger knows what it was raised against and the
 * exception rules have something to measure. A line typed by hand does not,
 * and nothing downstream can reconstruct it.
 */

export interface LineSourcesProps {
  readonly docKey: string
  readonly documentId: string
  readonly sources: readonly LineSourceDef[]
  readonly lines: readonly LineRow[]
  readonly onAdd: (lines: readonly LineRow[]) => void
}

export const LineSources = ({ docKey, documentId, sources, lines, onAdd }: LineSourcesProps) => {
  const api = useApi()
  const picker = useRef<HTMLInputElement>(null)
  const [pick, setPick] = useState<PickResult | null>(null)
  const [chosen, setChosen] = useState<Record<string, string>>({})
  const [imported, setImported] = useState<Awaited<ReturnType<typeof api.importLines>> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [onlyChosen, setOnlyChosen] = useState(false)
  const [hideFullyRequested, setHideFullyRequested] = useState(false)
  const [view, setView] = useState<'ITEM' | 'BOQ'>('ITEM')

  const offered = sources.filter((s) => s.source !== 'order')
  if (offered.length === 0) return null

  const load = useMutation({
    mutationFn: (source: string) => api.pickLines(docKey, documentId, source),
    onSuccess: (result) => {
      setError(null)
      // Everything already on the document is off the list: picking the same
      // BOQ line twice is how a quantity gets ordered twice.
      const already = new Set(lines.map((l) => String(l['fromLineId'] ?? '')))
      const fresh = result.lines.filter((l) => !l.fromLineId || !already.has(l.fromLineId))
      setPick({ ...result, lines: fresh })
      // Nothing is chosen until someone chooses it. Pre-filling every line at
      // its full open quantity meant one click requested the whole remaining
      // estimate — or issued every lot in the warehouse — which is exactly the
      // over-requesting the estimate exists to stop (docs/video-notes/06).
      setChosen({})
      setSearch('')
      setOnlyChosen(false)
      setHideFullyRequested(false)
      setView('ITEM')
    },
    onError: (err) => setError(messageFor(err)),
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await toBase64(file)
      return api.importLines(docKey, documentId, base64)
    },
    onSuccess: (result) => {
      setError(null)
      setImported(result)
    },
    onError: (err) => setError(messageFor(err)),
  })

  /**
   * Fill in what each line is ready to be billed for.
   *
   * A line with nothing done is left blank rather than set to zero: blank
   * means "not on this bill", and zero would put an empty line on the
   * customer's copy.
   */
  const autoFill = (): void => {
    if (!pick) return
    const next: Record<string, string> = { ...chosen }
    for (const line of pick.lines) {
      if (line.calculatedQty === undefined) continue
      if (Number(line.calculatedQty) <= 0) continue
      next[keyOf(line)] = line.calculatedQty
    }
    setChosen(next)
  }

  /**
   * The rare case where everything open really is wanted: an explicit choice,
   * over the lines on screen (so a search narrows it), never the default.
   * A rate-only line has no quantity to fill.
   */
  const fillOpen = (): void => {
    const next: Record<string, string> = { ...chosen }
    for (const line of shown) {
      if (line.openQty === 'RO' || !(Number(line.openQty) > 0)) continue
      next[keyOf(line)] = line.openQty
    }
    setChosen(next)
  }

  const takePicked = (): void => {
    if (!pick) return
    const next: LineRow[] = []
    let seq = lines.length
    for (const line of pick.lines) {
      const quantity = chosen[keyOf(line)] ?? ''
      if (quantity === '' || Number(quantity) <= 0) continue
      seq += 1
      next.push({
        seq,
        itemId: line.itemId,
        itemLabel: line.itemLabel,
        description: line.description,
        unit: line.unit,
        quantity,
        rate: line.rate ?? '',
        // Set on the estimate and carried by the picker, so the person keying
        // the receipt never has to know which of 500 support items is billable.
        ...(line.isBillable === undefined ? {} : { isBillable: line.isBillable ? 'YES' : 'NO' }),
        ...(line.boqCode ? { boqRef: line.boqCode } : {}),
        // The term a line is billed under, which the picker has always
        // returned and this has always dropped — so the 'Billing term' column
        // was blank on every line the picker added, and the term had to be
        // read off the project instead of off the bill.
        ...(line.billingTermLineId ? { billingTermLineId: line.billingTermLineId } : {}),
        ...(line.hsnSacCode ? { hsnSacCode: line.hsnSacCode } : {}),
        ...(line.brand ? { brand: line.brand } : {}),
        ...(line.fromLineId ? { fromLineId: line.fromLineId } : {}),
        // Whatever else the picker says the row starts with (docs/24).
        ...(line.carry ?? {}),
      })
    }
    if (next.length > 0) onAdd([...lines, ...next])
    setPick(null)
  }

  const takeImported = (): void => {
    if (!imported) return
    let seq = lines.length
    const next = imported.accepted.map((line) => ({ ...line, seq: ++seq }) as LineRow)
    if (next.length > 0) onAdd([...lines, ...next])
    setImported(null)
  }

  /** What has a quantity against it right now — the footer's own count. */
  const takingCount = pick
    ? pick.lines.filter((l) => Number(chosen[keyOf(l)] ?? '0') > 0).length
    : 0

  const shown = pick
    ? pick.lines.filter((l) => {
      if (onlyChosen && !(Number(chosen[keyOf(l)] ?? '0') > 0)) return false
      if (hideFullyRequested && Number(l.openQty) <= 0) return false
      const q = search.trim().toLowerCase()
      if (q === '') return true
      return `${l.itemLabel} ${l.description ?? ''} ${l.note ?? ''}`.toLowerCase().includes(q)
    })
    : []

  // Stock has nothing estimated about it; the estimate and the BOQ do.
  const estimated = pick !== null && pick.source !== 'stock'

  /**
   * The same lines, nested under the BOQ line they were estimated against.
   *
   * Built from what is already showing, so the search and both checkboxes
   * apply in either view; a group that nothing matches keeps its place in the
   * count but is not drawn, which is what the `n of m` reads against.
   */
  const groups = (() => {
    const byKey = new Map<string, {
      key: string
      ref: string | null
      narrative: string
      tendered: boolean | undefined
      lines: PickLine[]
    }>()
    for (const line of pick?.lines ?? []) {
      const key = line.boqLineId ?? '—'
      const existing = byKey.get(key)
      if (existing) continue
      byKey.set(key, {
        key,
        ref: line.boqCode ?? null,
        narrative: line.boqDescription ?? 'Not against any one BOQ line',
        tendered: line.tendered,
        lines: [],
      })
    }
    for (const line of shown) {
      byKey.get(line.boqLineId ?? '—')?.lines.push(line)
    }
    return [...byKey.values()]
  })()

  const matched = groups.filter((g) => g.lines.length > 0).length

  /** Only the BOQ picker knows how much of a line is ready to be billed. */
  const calculable = (pick?.lines ?? []).some((l) => l.calculatedQty !== undefined)
  /** Only the buying documents are told what is already on the shelf. */
  const stocked = (pick?.lines ?? []).some((l) => l.stockOnHand !== undefined)


  const boqColumns: ColumnDef<PickLine>[] = estimated
    ? [
      { key: 'boqCode', header: 'Ref no', width: 110, render: (row) => row.boqCode ?? '—' },
      /*
        Tendered or not (docs/05 §5b). `NT` is extra work outside the original
        tender: it is picked the same way, but it is what Regularise BOQ later
        has to account for, so the person picking should be able to see which
        it is before they do.
      */
      { key: 'tendered', header: 'BOQ', width: 70,
        render: (row) => (row.tendered === false ? 'NT' : 'T') },
    ]
    : []

  const columns: ColumnDef<PickLine>[] = [
    ...boqColumns,
    { key: 'itemLabel', header: 'Material', width: 420,
      render: (row) => (
        <>
          <div>{row.itemLabel}</div>
          {row.description ? <div className="cell-sub">{row.description}</div> : null}
          {row.note ? <div className="cell-sub">{row.note}</div> : null}
        </>
      ) },
    { key: 'unit', header: 'Unit', width: 90 },
    { key: 'availableQty', header: estimated ? 'Estimated' : 'In total', width: 120, align: 'right',
      render: (row) => <span className="num">{formatQuantity(row.availableQty)}</span> },
    /*
      What has already been raised against this line, from wherever (docs/15
      R-4.3). Without it, "still open" is a number with nothing to read it
      against: 40 left of 40 and 40 left of 400 are the same figure and very
      different situations.
    */
    { key: 'requested', header: estimated ? 'So far requested' : 'Already taken', width: 150, align: 'right',
      render: (row) => (
        <span className="num">
          {/* A rate-only line has no quantity to have used up. */}
          {row.availableQty === 'RO' ? '—' : formatQuantity(requestedSoFar(row))}
        </span>
      ) },
    { key: 'openQty', header: 'Still open', width: 120, align: 'right',
      render: (row) => <span className="num">{formatQuantity(row.openQty)}</span> },
    /*
      What is already on the shelf. Asked here rather than in a report because
      it is asked WITH the line in front of you: "can check stock in warehouse"
      is the step that stops a site ordering what it already has
      (docs/video-notes/06 §9).
    */
    ...(stocked
      ? [{ key: 'stockOnHand', header: 'In stock', width: 116, align: 'right' as const,
        render: (row: PickLine) => (
          <span className="num">
            {row.stockOnHand === undefined ? '—' : formatQuantity(row.stockOnHand)}
          </span>
        ) }]
      : []),
    /*
      How much of this line has actually been done — goods received for supply,
      work progress for installation, billable estimates only. It is the figure
      the vendor calls the Calculated Quantity, and the one `Auto fill` puts
      in. It advises rather than caps: a mobilisation advance is claimed
      against a line that shows no progress at all.
    */
    ...(calculable
      ? [{ key: 'calculatedQty', header: 'Ready to bill', width: 140, align: 'right' as const,
        render: (row: PickLine) => (
          <span className="num">
            {row.calculatedQty === undefined ? '—' : formatQuantity(row.calculatedQty)}
          </span>
        ) }]
      : []),
    { key: 'rate', header: 'Rate', width: 120, align: 'right',
      render: (row) => <span className="num">{row.rate ? formatRate(row.rate) : '—'}</span> },
    { key: 'take', header: 'Take', width: 140,
      render: (row) => (
        <input
          className="control control--right"
          inputMode="decimal"
          aria-label={`Quantity to take of ${row.itemLabel}`}
          value={chosen[keyOf(row)] ?? ''}
          onChange={(e) => {
            // Read before the updater runs: React clears currentTarget once the
            // handler returns, so touching it inside setState throws on null.
            const value = e.currentTarget.value
            setChosen((current) => ({ ...current, [keyOf(row)]: value }))
          }}
        />
      ) },
  ]

  // The ref and the T/NT flag are in the group's own heading in this view.
  const nestedColumns = columns.filter((c) => c.key !== 'boqCode' && c.key !== 'tendered')

  return (
    <div className="line-sources">
      {offered.map((source) => (
        source.source === 'excel' ? (
          <span key={source.key} style={{ display: 'inline-flex', gap: 10 }}>
            <Button
              kind="ghost"
              icon={<Icon name="up" size={18} />}
              disabled={upload.isPending}
              onClick={() => picker.current?.click()}
            >
              {upload.isPending ? 'Reading…' : source.label}
            </Button>
            {/*
              Export and Import are the same file (docs/13): what comes down is
              filled in with what is already on this document, so a round trip
              through Excel is how a BOQ is edited in bulk.
            */}
            <a className="btn btn--ghost" href={api.importTemplateUrl(docKey, documentId)} download>
              <Icon name="dl" size={18} />
              <span>{docKey === 'project' ? 'Export to Excel' : 'Template'}</span>
            </a>
          </span>
        ) : (
          <Button
            key={source.key}
            kind="ghost"
            icon={<Icon name="clip" size={18} />}
            disabled={load.isPending}
            onClick={() => load.mutate(source.source)}
          >
            {load.isPending ? 'Looking…' : source.label}
          </Button>
        )
      ))}

      <input
        ref={picker}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0]
          if (file) upload.mutate(file)
          e.currentTarget.value = ''
        }}
      />

      {error ? <div style={{ marginTop: 12 }}><Notice kind="bad">{error}</Notice></div> : null}

      {pick ? (
        <Modal
          title={pick.title}
          wide
          onClose={() => setPick(null)}
          footerLeft={
            pick.lines.length === 0
              ? undefined
              : takingCount === 0
                ? 'No entries are selected'
                : `${takingCount} of ${pick.lines.length} selected`
          }
          actions={
            <>
              {/*
                One button that fills every line with what is ready to be
                billed. On a BOQ of four hundred lines, typing each figure by
                hand is the difference between raising a running bill in a
                minute and raising it in an afternoon — and it is where the
                quantities go wrong (docs/video-notes/06 §7).
              */}
              {calculable ? (
                <Button kind="ghost" onClick={autoFill}>Auto fill qty</Button>
              ) : (
                <Button kind="ghost" disabled={shown.length === 0} onClick={fillOpen}>
                  Fill all open quantities
                </Button>
              )}
              <Button kind="ghost" onClick={() => setPick(null)}>Cancel</Button>
              <Button kind="primary" disabled={takingCount === 0} onClick={takePicked}>
                Add what I have entered
              </Button>
            </>
          }
        >
          {pick.emptyReason ? (
            <Notice kind="info">{pick.emptyReason}</Notice>
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 14 }}>
                Type a quantity against each line you need. Lines left blank are not added.
              </p>
              {/*
                A stock picker on a real site runs to hundreds of lots and a BOQ
                picker to hundreds of lines. Without a search, choosing one of
                them means scrolling for it (docs/02 §6b).
              */}
              <div className="drive-toolbar">
                <div style={{ flex: 1 }}>
                  <Input
                    value={search}
                    onChange={setSearch}
                    placeholder="Search by material, description or brand"
                  />
                </div>
                {estimated ? (
                  /*
                    `Item View` / `BOQ View` (docs/05 §5b, docs/16 AC-3.4). The
                    flat list is how somebody works down a list of materials;
                    the nested one is how they answer "what does this BOQ line
                    actually need", which is a different question and the one
                    the narrative text is there for.
                  */
                  <div className="segmented">
                    <button
                      type="button"
                      className={view === 'ITEM' ? 'segmented__on' : ''}
                      onClick={() => setView('ITEM')}
                    >
                      Item view
                    </button>
                    <button
                      type="button"
                      className={view === 'BOQ' ? 'segmented__on' : ''}
                      onClick={() => setView('BOQ')}
                    >
                      BOQ view
                    </button>
                  </div>
                ) : null}
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={onlyChosen}
                    onChange={() => setOnlyChosen((on) => !on)}
                  />
                  <span>Show only what I have entered</span>
                </label>
                {estimated ? (
                  <label className="column-choice">
                    <input
                      type="checkbox"
                      checked={hideFullyRequested}
                      onChange={() => setHideFullyRequested((on) => !on)}
                    />
                    <span>Don't show fully requested</span>
                  </label>
                ) : null}
              </div>
              {view === 'BOQ' && estimated ? (
                <>
                  <p className="helper">
                    {matched} of {groups.length} BOQ line{groups.length === 1 ? '' : 's'} match
                  </p>
                  {groups.filter((g) => g.lines.length > 0).map((group) => (
                    <div key={group.key} className="pick-group">
                      <h3 className="subhead">
                        {group.ref ? <span className="mono">{group.ref}</span> : null}{' '}
                        {group.narrative}
                        {group.tendered === false ? ' · NT' : ''}
                      </h3>
                      <Table columns={nestedColumns} rows={group.lines} getKey={keyOf} />
                    </div>
                  ))}
                </>
              ) : (
                <Table columns={columns} rows={shown} getKey={keyOf} />
              )}
              {shown.length === 0 ? (
                <p className="helper">Nothing matches that.</p>
              ) : null}
            </>
          )}
        </Modal>
      ) : null}

      {imported ? (
        <Modal
          title="Imported from the spreadsheet"
          onClose={() => setImported(null)}
          footerLeft={imported.summary}
          actions={
            <>
              <Button kind="ghost" onClick={() => setImported(null)}>Cancel</Button>
              <Button kind="primary" disabled={imported.accepted.length === 0} onClick={takeImported}>
                Add {imported.accepted.length} line{imported.accepted.length === 1 ? '' : 's'}
              </Button>
            </>
          }
        >
          {imported.ignoredHeaders.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Notice kind="warn">
                These columns are not on this document and were left out:{' '}
                {imported.ignoredHeaders.join(', ')}.
              </Notice>
            </div>
          ) : null}

          {imported.rejected.length > 0 ? (
            <div>
              <p style={{ marginBottom: 10 }}>
                <strong>{imported.rejected.length}</strong> row
                {imported.rejected.length === 1 ? '' : 's'} could not be read. Fix them in the
                spreadsheet and import again — the rest can be added now.
              </p>
              <ul className="import-problems">
                {imported.rejected.slice(0, 12).map((row) => (
                  <li key={row.sheetRow}>
                    <span className="num">Row {row.sheetRow}</span> — {row.problem}
                  </li>
                ))}
              </ul>
              {imported.rejected.length > 12 ? (
                <p className="muted">…and {imported.rejected.length - 12} more.</p>
              ) : null}
            </div>
          ) : (
            <p>Every row was read. Nothing was rejected.</p>
          )}
        </Modal>
      ) : null}
    </div>
  )
}

const keyOf = (line: PickLine): string => line.fromLineId ?? line.itemId ?? line.itemLabel

/**
 * How much of this line has already been asked for.
 *
 * The server sends what the source holds and what is still open; the
 * difference is what everything raised against it so far adds up to. Computed
 * rather than sent so there is one place the two can never disagree.
 */
const requestedSoFar = (line: PickLine): string => {
  if (line.availableQty === 'RO' || line.openQty === 'RO') return ZERO_QTY
  const taken = subQty(qty(line.availableQty), qty(line.openQty))
  return Number(taken) < 0 ? ZERO_QTY : taken
}

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read'))
    reader.onload = () => {
      const result = String(reader.result)
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })
